import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  UserIcon,
  XCircleIcon,
  CheckCircleIcon,
  KeyIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { studentAPI, batchAPI } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import PermissionGate from '../PermissionGate';
import ConfirmationModal from '../ConfirmationModal';
import { useDeactivateStudent, useActivateStudent } from '../../hooks/useStudents';

const Students = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [showPortalModal, setShowPortalModal] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const queryClient = useQueryClient();
  const searchInputRef = useRef(null);
  const { Permission } = usePermissions();

  // Debounce the search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Track cursor position and focus state
  const [cursorPosition, setCursorPosition] = useState(0);
  const [shouldMaintainFocus, setShouldMaintainFocus] = useState(false);

  // Maintain focus when search results update
  useEffect(() => {
    if (shouldMaintainFocus && searchInputRef.current) {
      // Use a small delay to ensure the DOM has updated
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          const position = Math.min(cursorPosition, searchTerm.length);
          searchInputRef.current.setSelectionRange(position, position);
        }
      }, 10);
      
      return () => clearTimeout(timer);
    }
  }, [debouncedSearchTerm, shouldMaintainFocus, cursorPosition, searchTerm.length]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCursorPosition(e.target.selectionStart);
    setShouldMaintainFocus(true);
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShouldMaintainFocus(true);
  };

  // Handle search input blur
  const handleSearchBlur = () => {
    setShouldMaintainFocus(false);
  };

  // Handle batch filter change
  const handleBatchChange = (e) => {
    setSelectedBatch(e.target.value);
    setCurrentPage(0); // Reset to first page when filter changes
  };

  // Fetch all batches for the filter dropdown
  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches-for-filter'],
    queryFn: () => batchAPI.getAll(0, 1000, true).then(res => res.data),
  });

  const { data: students, isLoading, error } = useQuery({
    queryKey: ['students', currentPage, debouncedSearchTerm, selectedBatch],
    queryFn: () => {
      if (debouncedSearchTerm) {
        return studentAPI.search(debouncedSearchTerm).then(res => res.data);
      }
      return studentAPI.getAll(currentPage * itemsPerPage, itemsPerPage, true, selectedBatch || null).then(res => res.data);
    },
  });

  const { data: totalCount, isLoading: countLoading } = useQuery({
    queryKey: ['students-count', selectedBatch],
    queryFn: () => studentAPI.getCount().then(res => res.data.count),
    enabled: !debouncedSearchTerm && !selectedBatch, // Only fetch total count when not searching and not filtering by batch
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => studentAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['students']);
    },
  });

  const deactivateMutation = useDeactivateStudent();
  const activateMutation = useActivateStudent();

  // Portal management mutations
  const createPortalMutation = useMutation({
    mutationFn: (studentId) => studentAPI.createPortalAccess(studentId),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['students']);
      setShowPortalModal(false);
      setPortalTarget(null);
      // Show the generated password to admin
      setGeneratedPassword(response.data.initial_password);
      setShowPasswordModal(true);
    },
  });

  const revokePortalMutation = useMutation({
    mutationFn: (studentId) => studentAPI.revokePortalAccess(studentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['students']);
      setShowRevokeModal(false);
      setRevokeTarget(null);
    },
  });

  const handleDelete = (id, studentName) => {
    setDeleteTarget({ id, studentName });
    setShowDeleteModal(true);
  };

  const handleDeactivate = (id, studentName, isActive) => {
    setDeactivateTarget({ id, studentName, isActive });
    setShowDeactivateModal(true);
  };

  const handleStatusToggle = () => {
    if (deactivateTarget) {
      if (deactivateTarget.isActive) {
        deactivateMutation.mutate(deactivateTarget.id);
      } else {
        activateMutation.mutate(deactivateTarget.id);
      }
      setShowDeactivateModal(false);
    }
  };

  const handleCreatePortal = (studentId, studentName) => {
    setPortalTarget({ id: studentId, name: studentName });
    setShowPortalModal(true);
  };

  const handleConfirmCreatePortal = () => {
    if (portalTarget) {
      createPortalMutation.mutate(portalTarget.id);
    }
  };

  const handleRevokePortal = (studentId, studentName) => {
    setRevokeTarget({ id: studentId, name: studentName });
    setShowRevokeModal(true);
  };

  const handleConfirmRevokePortal = () => {
    if (revokeTarget) {
      revokePortalMutation.mutate(revokeTarget.id);
    }
  };

  const filteredStudents = students || [];

  if (isLoading || countLoading || batchesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Error loading students: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Students</h1>
          <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">Manage student information and records</p>
        </div>
        <PermissionGate permission={Permission.CREATE_STUDENT}>
          <Link
            to="/students/new"
            className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-800 transition-colors"
          >
            <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Student</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </PermissionGate>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-500 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              placeholder="Search students by name, ID, or contact..."
            />
          </div>
          
          {/* Batch Filter */}
          <div className="relative">
            <select
              value={selectedBatch}
              onChange={handleBatchChange}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            >
              <option value="">All Batches</option>
              {batches?.map((batch) => (
                <option key={batch._id || batch.id} value={batch._id || batch.id}>
                  {batch.batch_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Students List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <h2 className="text-base sm:text-lg font-semibold text-white">
            {debouncedSearchTerm || selectedBatch
              ? `${filteredStudents.length} Student${filteredStudents.length !== 1 ? 's' : ''}`
              : `${totalCount || 0} Student${(totalCount || 0) !== 1 ? 's' : ''}`
            }
            {selectedBatch && !debouncedSearchTerm && (
              <span className="text-sm font-normal text-gray-400 ml-2">
                in {batches?.find(b => (b._id || b.id) === selectedBatch)?.batch_name}
              </span>
            )}
          </h2>
        </div>
        
        {filteredStudents.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <UserIcon className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-300">No students found</h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-400">
              {searchTerm ? 'Try adjusting your search terms.' : 
               selectedBatch ? 'No students found in this batch.' : 
               'Get started by creating a new student.'}
            </p>
            <PermissionGate permission={Permission.CREATE_STUDENT}>
              {!searchTerm && (
                <div className="mt-4 sm:mt-6">
                  <Link
                    to="/students/new"
                    className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                    Add Student
                  </Link>
                </div>
              )}
            </PermissionGate>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Student Info
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Contact
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Portal Access
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {filteredStudents.map((student) => (
                  <tr key={student._id || student.id} className="hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10">
                          {student.profile_picture_base64 ? (
                            <img
                              src={`data:${student.profile_picture_content_type || 'image/jpeg'};base64,${student.profile_picture_base64}`}
                              alt={`${student.first_name} ${student.last_name}`}
                              className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-blue-600 flex items-center justify-center">
                              <span className="text-xs sm:text-sm font-medium text-white">
                                {student.first_name?.[0]}{student.last_name?.[0]}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <div className="text-sm font-medium text-white">
                            {student.first_name} {student.last_name}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-400">
                            ID: {student.student_id}
                          </div>
                          {/* Mobile-only contact info */}
                          <div className="sm:hidden text-xs text-gray-400 mt-1">
                            {student.email}
                          </div>
                          {/* Mobile-only status info */}
                          <div className="md:hidden text-xs text-gray-400 mt-2">
                            <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                              student.is_active
                                ? 'bg-green-900/30 text-green-300 border border-green-700'
                                : 'bg-red-900/30 text-red-300 border border-red-700'
                            }`}>
                              {student.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                      <div>{student.email}</div>
                      <div className="text-xs sm:text-sm text-gray-400">{student.phone}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        student.is_active
                          ? 'bg-green-900/30 text-green-300 border border-green-700'
                          : 'bg-red-900/30 text-red-300 border border-red-700'
                      }`}>
                        {student.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden lg:table-cell">
                      {student.has_portal_access ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-900/30 text-blue-300 border border-blue-700">
                          <ShieldCheckIcon className="h-4 w-4" />
                          Enabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-gray-700 text-gray-400 border border-gray-600">
                          <ShieldExclamationIcon className="h-4 w-4" />
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 sm:space-x-2">
                        <PermissionGate permission={Permission.READ_STUDENT}>
                          <Link
                            to={`/students/${student._id || student.id}`}
                            className="p-1 sm:p-2 text-blue-400 hover:text-blue-300 rounded-md hover:bg-gray-700 transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </Link>
                        </PermissionGate>
                        <PermissionGate permission={Permission.UPDATE_STUDENT}>
                          <Link
                            to={`/students/${student._id || student.id}/edit`}
                            className="p-1 sm:p-2 text-green-400 hover:text-green-300 rounded-md hover:bg-gray-700 transition-colors"
                            title="Edit Student"
                          >
                            <PencilIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </Link>
                        </PermissionGate>
                        <PermissionGate permission={Permission.UPDATE_STUDENT}>
                          <button
                            onClick={() => handleDeactivate(student._id || student.id, `${student.first_name} ${student.last_name}`, student.is_active)}
                            className={`p-1 sm:p-2 rounded-md hover:bg-gray-700 transition-colors ${
                              student.is_active
                                ? 'text-orange-400 hover:text-orange-300'
                                : 'text-emerald-400 hover:text-emerald-300'
                            }`}
                            title={student.is_active ? "Deactivate Student" : "Activate Student"}
                          >
                            {student.is_active ? (
                              <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            ) : (
                              <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            )}
                          </button>
                        </PermissionGate>
                        <PermissionGate permission={Permission.UPDATE_STUDENT}>
                          {student.has_portal_access ? (
                            <button
                              onClick={() => handleRevokePortal(student._id || student.id, `${student.first_name} ${student.last_name}`)}
                              className="p-1 sm:p-2 text-purple-400 hover:text-purple-300 rounded-md hover:bg-gray-700 transition-colors"
                              title="Revoke Portal Access"
                            >
                              <ShieldExclamationIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleCreatePortal(student._id || student.id, `${student.first_name} ${student.last_name}`)}
                              className="p-1 sm:p-2 text-blue-400 hover:text-blue-300 rounded-md hover:bg-gray-700 transition-colors"
                              title="Create Portal Access"
                            >
                              <KeyIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </button>
                          )}
                        </PermissionGate>
                        <PermissionGate permission={Permission.DELETE_STUDENT}>
                          <button
                            onClick={() => handleDelete(student._id || student.id, `${student.first_name} ${student.last_name}`)}
                            className="p-1 sm:p-2 text-red-400 hover:text-red-300 rounded-md hover:bg-gray-700 transition-colors"
                            title="Delete Student"
                          >
                            <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!searchTerm && !selectedBatch && filteredStudents.length > 0 && (
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-400 text-center sm:text-left">
              Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalCount || 0)} of {totalCount || 0} results
            </div>
            <div className="flex justify-center sm:justify-end space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={(currentPage + 1) * itemsPerPage >= (totalCount || 0)}
                className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setShowDeleteModal(false);
          }
        }}
        title="Confirm Deletion"
        message={`Are you sure you want to delete ${deleteTarget?.studentName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        confirmButtonVariant="danger"
      />

      {/* Deactivate/Activate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={handleStatusToggle}
        title={deactivateTarget?.isActive ? "Confirm Deactivation" : "Confirm Activation"}
        message={
          deactivateTarget?.isActive
            ? `Are you sure you want to deactivate ${deactivateTarget?.studentName}? They will not appear in attendance or analytics while deactivated. You can reactivate them later.`
            : `Are you sure you want to activate ${deactivateTarget?.studentName}? They will appear in attendance and analytics again.`
        }
        confirmText={deactivateTarget?.isActive ? "Deactivate" : "Activate"}
        cancelText="Cancel"
        type={deactivateTarget?.isActive ? "danger" : "success"}
        confirmButtonVariant={deactivateTarget?.isActive ? "danger" : "success"}
      />

      {/* Create Portal Access Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPortalModal}
        onClose={() => {
          setShowPortalModal(false);
          setPortalTarget(null);
        }}
        onConfirm={handleConfirmCreatePortal}
        title="Create Portal Access"
        message={`Are you sure you want to create portal access for ${portalTarget?.name}? A secure password will be auto-generated and sent to their email.`}
        confirmText={createPortalMutation.isPending ? 'Creating...' : 'Create Access'}
        cancelText="Cancel"
        type="info"
        confirmButtonVariant="primary"
      />

      {/* Show Generated Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <KeyIcon className="h-8 w-8 text-green-400" />
              <h3 className="text-xl font-bold text-white">Portal Access Created!</h3>
            </div>
            <div className="mb-6">
              <p className="text-gray-300 mb-4">
                Portal access has been created successfully. Here is the auto-generated password:
              </p>
              <div className="bg-gray-900 border border-gray-600 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <code className="text-2xl font-mono text-blue-400 tracking-wider">
                    {generatedPassword}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPassword);
                      alert('Password copied to clipboard!');
                    }}
                    className="ml-3 p-2 text-gray-400 hover:text-white rounded-md hover:bg-gray-700 transition-colors"
                    title="Copy to clipboard"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="mt-4 bg-blue-900/20 border border-blue-700 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  ℹ️ This password has been sent to the student's email. They will be required to change it on first login.
                </p>
              </div>
              <div className="mt-3 bg-yellow-900/20 border border-yellow-700 rounded-lg p-3">
                <p className="text-yellow-300 text-sm">
                  ⚠️ Please save this password securely. It won't be shown again.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setGeneratedPassword('');
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Revoke Portal Access Modal */}
      <ConfirmationModal
        isOpen={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        onConfirm={handleConfirmRevokePortal}
        title="Revoke Portal Access"
        message={`Are you sure you want to revoke portal access for ${revokeTarget?.name}? They will no longer be able to login to the student portal.`}
        confirmText="Revoke Access"
        cancelText="Cancel"
        type="danger"
        confirmButtonVariant="danger"
      />
    </div>
  );
};

export default Students; 